import { useState, useEffect, useRef } from "react";
import { VerticalVideoOverlayPlayer } from "../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { categoryObj } from "../../../ynet1280/newYnetTvVerticalDynamic/components/newYnetTvVerticalDynamicComponenta";
import { RylzMediaItemDesktop } from "../../../verticalVideoOverlayPlayer/designs/rylzMediaItem/rylzMediaItemDesktop";
import { PromoCarousel } from "../../../ynet1280/newYnetTvVerticalDynamic/components/promoCarousel";
import { PromoCarouselLtr } from "../../../newYnetLTRTvVerticalDynamic/components/promoCarouselLtr";
import { isWcmEditor } from "../../../../../config/serverConfig";
import { addAxiosDateTransformerSettings } from "../../../../../utils/network/axiosDateTransformer";
import { ItemJsonVer2 } from "../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { SiteItemData } from "../../../../interfaces";
import * as axios from "axios";
import { disablePageRefresh, enablePageRefresh } from "../../../../siteWidgets/siteWidgetUtils";
import { RylzComponentConfig, RylzDisplayItem, RylzInitialItemData, mapInitialItemToDisplayItem, mapItemToDisplayItem, mapItemToOverlayItem, } from "./rylzDisplay";
import { RylzDesktopItem } from "./rylzDesktopItem";
import { applyPrerollOnVideoEntrance, findFeedIndexForItem, getRylzListWithAds } from "../utils/rylzFeedAds";

interface RylzDesktopProps {
    jsonPageId: string
    isLTR?: boolean
    domain?: string
    category?: categoryObj
    languageCode?: string
    imagesDomain?: string
    initialItems?: RylzInitialItemData[]
    initialConfig?: RylzComponentConfig
}

export const RylzDesktop = (props: RylzDesktopProps) => {
    const { jsonPageId, domain, isLTR, languageCode, category, imagesDomain, initialItems, initialConfig } = props;
    const mappedInitialItems = (initialItems || []).map(item => mapInitialItemToDisplayItem(item, imagesDomain));
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    const [isPromoCarouselInView, setIsPromoCarouselInView] = useState(false);
    const [items, setItems] = useState<RylzDisplayItem[]>(mappedInitialItems);
    const [overlayItems, setOverlayItems] = useState<SiteItemData[]>([]);
    const [config] = useState<RylzComponentConfig>(initialConfig || {});
    const [currentPromoIndex, setCurrentPromoIndex] = useState(-1);
    const [lastNonHoveredPromoIndex, setLastNonHoveredPromoIndex] = useState<number | null>(null);
    const [disableAutoScrollIntoView, setDisableAutoScrollIntoView] = useState(false);
    const containerRef = useRef(null) as any;
    const carouselRef = useRef(null) as any;
    const currentPromoIndexRef = useRef(-1) as any;
    currentPromoIndexRef.current = currentPromoIndex;
    const IMAGE_WIDTH = 219;
    const ITEM_MARGIN = 15;

    const isTabVisible = config.isTabVisible;
    const tabTitleText = config.title;
    const tabSubTitleText = config.feedButtonTitle;
    const hideTabMoreButton = config.hideFeedButton;
    const tabBackgroundColor = config.backgroundColor;
    const tabFontColor = config.fontColor;
    const showVideoDuration = !!config.showVideoDuration;
    const imageAutoAdvanceSeconds = config.imageAutoAdvanceSeconds || 0;
    const backgroundColorLabel = config.backgroundColorLabel || "#FFFFFF";
    const showPrerollAds = !!config.showPrerollAds;
    const prerollOnVideoEntrance = !!config.prerollOnVideoEntrance;
    const firstAdPlace = config.firstAdPlace || 0;
    const maxAds = config.maxAds || 0;
    const repeatAds = config.repeatAds || 0;

    useEffect(() => {
        if (!jsonPageId || isWcmEditor()) return;
        const axiosInstance = axios.create(addAxiosDateTransformerSettings());

        axiosInstance.get(`/iphone/json/mobile/v2/page/${jsonPageId}`)
            .then((res: any) => {
                const rylzComponent = res.data.components.find((component) => component.name === "ynetRylz");
                if (!rylzComponent) return;
                const apiItems: ItemJsonVer2[] = rylzComponent.items || [];
                const videoBehavior = rylzComponent.videoBehavior || (initialConfig && initialConfig.videoBehavior);
                const autoAdvance = (rylzComponent.imageAutoAdvanceSeconds != null)
                    ? rylzComponent.imageAutoAdvanceSeconds
                    : imageAutoAdvanceSeconds;
                setItems(apiItems.map(mapItemToDisplayItem));
                setOverlayItems(apiItems.map((i, index) => mapItemToOverlayItem(i, videoBehavior, autoAdvance, index)));
            })
            .catch(err => console.error("RylzDesktop: error getting rylz data:", err));
    }, [jsonPageId]);

    const getInitialPromoIndex = (): number => {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item) continue;
            if (item.promoPath) return i;
            if (!item.isVideo && imageAutoAdvanceSeconds > 0) return i;
        }
        return -1;
    };

    useEffect(() => {
        if (items.length > 0) {
            setCurrentPromoIndex(getInitialPromoIndex());
        }
    }, [items]);

    useEffect(() => {
        if (isWcmEditor()) return;

        const observer = new IntersectionObserver(
            (entries) => {
                setIsPromoCarouselInView(entries[0].isIntersecting);
            },
            { threshold: 0.5 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (disableAutoScrollIntoView || currentPromoIndex < 0 || !containerRef.current || !carouselRef.current) return;

        const itemElement = document.getElementById(`rylzDesktopItem_${currentPromoIndex}`);
        const frameElement = containerRef.current.querySelector(".frame_forList") as HTMLElement | null;
        if (!itemElement || !frameElement) return;

        const itemRect = itemElement.getBoundingClientRect();
        const frameRect = frameElement.getBoundingClientRect();
        let delta = 0;
        if (itemRect.left < frameRect.left) {
            delta = itemRect.left - frameRect.left;
        } else if (itemRect.right > frameRect.right) {
            delta = itemRect.right - frameRect.right;
        }
        if (delta !== 0) {
            const duration = Math.min(700, Math.max(350, Math.abs(delta) / (IMAGE_WIDTH + ITEM_MARGIN) * 350));
            carouselRef.current.scrollByAnimated(delta, duration);
        }
    }, [currentPromoIndex, disableAutoScrollIntoView]);

    const onItemEnded = () => {
        const prev = currentPromoIndexRef.current as number;
        const nextIndex = prev + 1;
        if (nextIndex < items.length) {
            for (let i = nextIndex; i < items.length; i++) {
                const item = items[i];
                if (!item) continue;
                if (item.promoPath) {
                    setCurrentPromoIndex(i);
                    return;
                }
                if (!item.isVideo && imageAutoAdvanceSeconds > 0) {
                    setCurrentPromoIndex(i);
                    return;
                }
            }
        }
        setCurrentPromoIndex(-1);
    };

    const onOpenFeed = (originalIndex: number) => {
        if (overlayItems.length === 0) return;
        const listWithAds = getListWithAds();
        const initialFeedSlide = findFeedIndexForItem(listWithAds, overlayItems[originalIndex].itemId);
        const selected = initialFeedSlide >= 0 ? initialFeedSlide : originalIndex;
        setSelectedVideoIndex(selected);
        setCurrentPromoIndex(0);
        setIsPromoCarouselInView(false);
        setIsOverlayOpen(true);
        disablePageRefresh();
    };

    const openFeedFromTab = () => {
        if (overlayItems.length === 0) return;
        setSelectedVideoIndex(0);
        setIsOverlayOpen(true);
        disablePageRefresh();
    };

    const handleOverlayClose = () => {
        setIsOverlayOpen(false);
        setIsPromoCarouselInView(true);
        setSelectedVideoIndex(-1);
        enablePageRefresh();
    };

    const onPromoMouseHover = (hoveredIndex: number | null, videoRef?: HTMLVideoElement) => {
        if (hoveredIndex !== null) {
            setLastNonHoveredPromoIndex(currentPromoIndex);
            setCurrentPromoIndex(hoveredIndex);
        } else {
            if (currentPromoIndex !== lastNonHoveredPromoIndex && videoRef) videoRef.pause();
            setCurrentPromoIndex(lastNonHoveredPromoIndex != null ? lastNonHoveredPromoIndex : currentPromoIndex);
        }
    };

    const onUserArrowInteraction = () => {
        if (!disableAutoScrollIntoView) {
            setDisableAutoScrollIntoView(true);
        }
    };

    const pushGA4Event = (action: string, isOverlayOpenFlag: boolean) => {
        if (typeof window == "undefined" || !window.dataLayer) return;
        const actionPrefix = category && category.name ? (category.name === "דף הבית") ? "homepage" : category.name : "homepage";
        const gaAction = isOverlayOpenFlag ? `video_${action}` : `${actionPrefix}_${action}`;

        window.dataLayer.push({
            event: "content_click",
            vertical_video_action: gaAction,
        });
    };

    const getListWithAds = () => getRylzListWithAds({
        overlayItems,
        showPrerollAds,
        firstAdPlace,
        maxAds,
        repeatAds,
        allowNativeAds: true,
    });

    const getVideoItemsForOverlay = () => applyPrerollOnVideoEntrance(
        getListWithAds(),
        selectedVideoIndex,
        prerollOnVideoEntrance,
    );

    const PromoCarouselComponent = isLTR ? PromoCarouselLtr : PromoCarousel;

    return (
        <div className="rylz-component-desktop" ref={containerRef}>
            {isTabVisible &&
                <div className="rylzDesktopTab componentaTab" style={{ backgroundColor: tabBackgroundColor, color: tabFontColor || "#fff" }}>
                    <div className="right-side-title">{tabTitleText}</div>

                    {!hideTabMoreButton && tabSubTitleText &&
                        <div className="left-side-title" onClick={openFeedFromTab}>
                            <div className="title-text">{tabSubTitleText}</div>
                            <div className="arrow-icon"></div>
                        </div>
                    }
                </div>
            }

            <PromoCarouselComponent
                ref={carouselRef}
                imageWidth={IMAGE_WIDTH}
                marginRight={ITEM_MARGIN}
                pushGA4Event={pushGA4Event}
                itemListLength={items.length}
                onUserArrowInteraction={onUserArrowInteraction}
            >
                {items.map((item, index) =>
                    <RylzDesktopItem
                        key={index}
                        item={item}
                        itemIndex={index}
                        onItemEnded={onItemEnded}
                        onOpenFeed={onOpenFeed}
                        currentPromoIndex={currentPromoIndex}
                        onPromoMouseHover={onPromoMouseHover}
                        showVideoDuration={showVideoDuration}
                        backgroundColorLabel={backgroundColorLabel}
                        isPromoCarouselInView={isPromoCarouselInView}
                        imageAutoAdvanceSeconds={imageAutoAdvanceSeconds}
                    />
                )}
            </PromoCarouselComponent>

            <VerticalVideoOverlayPlayer
                isLTR={isLTR}
                domain={domain}
                isMobile={false}
                category={category}
                isTitleVisible={true}
                isSubTitleVisible={true}
                languageCode={languageCode}
                isOverlayOpen={isOverlayOpen}
                showPrerollAds={showPrerollAds}
                headerLogoClassName="rylz-header-logo"
                videoItems={getVideoItemsForOverlay()}
                selectedVideoIndex={selectedVideoIndex}
                onVideoOverlayClose={handleOverlayClose}
                ItemComponent={RylzMediaItemDesktop}
            />
        </div>
    );
};

RylzDesktop.siteScriptName = "RylzDesktop";
